import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelectCombobox } from '@/components/ui/multi-select-combobox';
import { Award, Baby, GraduationCap, Heart, Loader2, MapPin, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { maskCEP, maskTelefone, onlyDigits } from '@/lib/masks';
import { buscarCep } from '@/services/cep';
import { CursoPessoal, EstadoCivil, FichaCadastral, FichaFilho, Habilidade } from '@/types';

export const emptyFichaCadastral: FichaCadastral = {
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  estado_civil: null,
  telefone: '',
  data_nascimento: '',
  nome_conjuge: '',
  data_nascimento_conjuge: '',
  data_admissao: '',
  contato_emergencia_nome: '',
  contato_emergencia_telefone: '',
  contato_emergencia_parentesco: '',
  filhos: [],
  habilidades: [],
  cursos: [],
};

/** Monta um FichaCadastral a partir da resposta da API (pode vir null quando ainda não preenchida). */
export const fichaCadastralFromResponse = (ficha: Partial<FichaCadastral> | null | undefined): FichaCadastral => ({
  ...emptyFichaCadastral,
  ...ficha,
  cep: ficha?.cep ?? '',
  logradouro: ficha?.logradouro ?? '',
  numero: ficha?.numero ?? '',
  complemento: ficha?.complemento ?? '',
  bairro: ficha?.bairro ?? '',
  cidade: ficha?.cidade ?? '',
  estado: ficha?.estado ?? '',
  telefone: ficha?.telefone ?? '',
  data_nascimento: ficha?.data_nascimento ?? '',
  nome_conjuge: ficha?.nome_conjuge ?? '',
  data_nascimento_conjuge: ficha?.data_nascimento_conjuge ?? '',
  data_admissao: ficha?.data_admissao ?? '',
  contato_emergencia_nome: ficha?.contato_emergencia_nome ?? '',
  contato_emergencia_telefone: ficha?.contato_emergencia_telefone ?? '',
  contato_emergencia_parentesco: ficha?.contato_emergencia_parentesco ?? '',
  filhos: ficha?.filhos ?? [],
  habilidades: ficha?.habilidades ?? [],
  cursos: ficha?.cursos ?? [],
});

const ESTADO_CIVIL_LABELS: Record<EstadoCivil, string> = {
  solteiro: 'Solteiro(a)',
  casado: 'Casado(a)',
  uniao_estavel: 'União estável',
  divorciado: 'Divorciado(a)',
  viuvo: 'Viúvo(a)',
};

interface FichaCadastralFormProps {
  values: FichaCadastral;
  onChange: (patch: Partial<FichaCadastral>) => void;
  habilidadesCatalogo: Habilidade[];
  cursosCatalogo: CursoPessoal[];
  idPrefix?: string;
}

export function FichaCadastralForm({
  values,
  onChange,
  habilidadesCatalogo,
  cursosCatalogo,
  idPrefix = 'ficha',
}: FichaCadastralFormProps) {
  const [buscandoCep, setBuscandoCep] = useState(false);

  const handleCepChange = async (value: string) => {
    const masked = maskCEP(value);
    onChange({ cep: masked });

    if (onlyDigits(masked).length === 8) {
      setBuscandoCep(true);
      const endereco = await buscarCep(masked);
      setBuscandoCep(false);
      if (endereco) {
        onChange({
          logradouro: endereco.logradouro,
          bairro: endereco.bairro,
          cidade: endereco.cidade,
          estado: endereco.estado,
        });
      }
    }
  };

  const atualizarFilho = (index: number, patch: Partial<FichaFilho>) => {
    const filhos = values.filhos.map((f, i) => (i === index ? { ...f, ...patch } : f));
    onChange({ filhos });
  };

  const adicionarFilho = () => {
    onChange({ filhos: [...values.filhos, { nome: '', data_nascimento: null }] });
  };

  const removerFilho = (index: number) => {
    onChange({ filhos: values.filhos.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <MapPin className="h-4 w-4 text-muted-foreground" /> Endereço
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-cep`}>CEP</Label>
            <div className="relative">
              <Input
                id={`${idPrefix}-cep`}
                value={values.cep ?? ''}
                onChange={(e) => handleCepChange(e.target.value)}
                placeholder="00000-000"
                inputMode="numeric"
              />
              {buscandoCep && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-logradouro`}>Rua / Logradouro</Label>
            <Input
              id={`${idPrefix}-logradouro`}
              value={values.logradouro ?? ''}
              onChange={(e) => onChange({ logradouro: e.target.value })}
              placeholder="Rua, avenida..."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-numero`}>Número</Label>
            <Input
              id={`${idPrefix}-numero`}
              value={values.numero ?? ''}
              onChange={(e) => onChange({ numero: e.target.value })}
              placeholder="Nº"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-complemento`}>Complemento</Label>
            <Input
              id={`${idPrefix}-complemento`}
              value={values.complemento ?? ''}
              onChange={(e) => onChange({ complemento: e.target.value })}
              placeholder="Sala, andar..."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-bairro`}>Bairro</Label>
            <Input
              id={`${idPrefix}-bairro`}
              value={values.bairro ?? ''}
              onChange={(e) => onChange({ bairro: e.target.value })}
              placeholder="Bairro"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-cidade`}>Cidade</Label>
            <Input
              id={`${idPrefix}-cidade`}
              value={values.cidade ?? ''}
              onChange={(e) => onChange({ cidade: e.target.value })}
              placeholder="Cidade"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-estado`}>Estado (UF)</Label>
            <Input
              id={`${idPrefix}-estado`}
              value={values.estado ?? ''}
              onChange={(e) => onChange({ estado: e.target.value.toUpperCase().slice(0, 2) })}
              placeholder="UF"
              maxLength={2}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 border-t pt-6">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Heart className="h-4 w-4 text-muted-foreground" /> Dados pessoais
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-telefone`}>Telefone/celular</Label>
            <Input
              id={`${idPrefix}-telefone`}
              value={values.telefone ?? ''}
              onChange={(e) => onChange({ telefone: maskTelefone(e.target.value) })}
              placeholder="(00) 00000-0000"
              inputMode="numeric"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-data-nascimento`}>Data de nascimento</Label>
            <Input
              id={`${idPrefix}-data-nascimento`}
              type="date"
              value={values.data_nascimento ?? ''}
              onChange={(e) => onChange({ data_nascimento: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-estado-civil`}>Estado civil</Label>
            <Select
              value={values.estado_civil ?? undefined}
              onValueChange={(v) => onChange({ estado_civil: v as EstadoCivil })}
            >
              <SelectTrigger id={`${idPrefix}-estado-civil`}>
                <SelectValue placeholder="Selecionar..." />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ESTADO_CIVIL_LABELS) as EstadoCivil[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {ESTADO_CIVIL_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-data-admissao`}>Data de admissão</Label>
            <Input
              id={`${idPrefix}-data-admissao`}
              type="date"
              value={values.data_admissao ?? ''}
              onChange={(e) => onChange({ data_admissao: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-nome-conjuge`}>Nome do cônjuge</Label>
            <Input
              id={`${idPrefix}-nome-conjuge`}
              value={values.nome_conjuge ?? ''}
              onChange={(e) => onChange({ nome_conjuge: e.target.value })}
              placeholder="Nome completo"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-nascimento-conjuge`}>Data de nascimento do cônjuge</Label>
            <Input
              id={`${idPrefix}-nascimento-conjuge`}
              type="date"
              value={values.data_nascimento_conjuge ?? ''}
              onChange={(e) => onChange({ data_nascimento_conjuge: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 border-t pt-6">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" /> Contato de emergência
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-emergencia-nome`}>Nome</Label>
            <Input
              id={`${idPrefix}-emergencia-nome`}
              value={values.contato_emergencia_nome ?? ''}
              onChange={(e) => onChange({ contato_emergencia_nome: e.target.value })}
              placeholder="Nome completo"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-emergencia-telefone`}>Telefone</Label>
            <Input
              id={`${idPrefix}-emergencia-telefone`}
              value={values.contato_emergencia_telefone ?? ''}
              onChange={(e) => onChange({ contato_emergencia_telefone: maskTelefone(e.target.value) })}
              placeholder="(00) 00000-0000"
              inputMode="numeric"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-emergencia-parentesco`}>Parentesco</Label>
            <Input
              id={`${idPrefix}-emergencia-parentesco`}
              value={values.contato_emergencia_parentesco ?? ''}
              onChange={(e) => onChange({ contato_emergencia_parentesco: e.target.value })}
              placeholder="Ex: Mãe, cônjuge..."
            />
          </div>
        </div>
      </div>

      <div className="space-y-3 border-t pt-6">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Baby className="h-4 w-4 text-muted-foreground" /> Filhos
          </h3>
          <Button type="button" variant="outline" size="sm" onClick={adicionarFilho}>
            <Plus className="mr-1 h-4 w-4" />
            Adicionar filho
          </Button>
        </div>
        {values.filhos.length === 0 && <p className="text-sm text-muted-foreground">Nenhum filho cadastrado.</p>}
        {values.filhos.map((filho, index) => (
          <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-filho-nome-${index}`}>Nome</Label>
              <Input
                id={`${idPrefix}-filho-nome-${index}`}
                value={filho.nome}
                onChange={(e) => atualizarFilho(index, { nome: e.target.value })}
                placeholder="Nome do filho(a)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-filho-nascimento-${index}`}>Data de nascimento</Label>
              <Input
                id={`${idPrefix}-filho-nascimento-${index}`}
                type="date"
                value={filho.data_nascimento ?? ''}
                onChange={(e) => atualizarFilho(index, { data_nascimento: e.target.value })}
              />
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => removerFilho(index)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>

      <div className="space-y-2 border-t pt-6">
        <Label className="flex items-center gap-2 text-sm font-semibold">
          <Award className="h-4 w-4 text-muted-foreground" /> Habilidades
        </Label>
        <MultiSelectCombobox
          items={habilidadesCatalogo}
          value={values.habilidades}
          onChange={(habilidades) => onChange({ habilidades })}
          placeholder="Adicionar habilidade..."
        />
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-sm font-semibold">
          <GraduationCap className="h-4 w-4 text-muted-foreground" /> Cursos
        </Label>
        <MultiSelectCombobox
          items={cursosCatalogo}
          value={values.cursos}
          onChange={(cursos) =>
            onChange({
              cursos: cursos.map((c) => ({
                ...c,
                data_conclusao: values.cursos.find((existing) => existing.id === c.id)?.data_conclusao ?? null,
              })),
            })
          }
          placeholder="Adicionar curso..."
        />
      </div>
    </div>
  );
}
